import 'bootstrap/dist/css/bootstrap.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@/utils/globalLoader';
import theme from '@/theme';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from 'styled-components';
import { Provider as ReduxProvider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { toast } from 'react-toastify';
import history from './utils/history';
import { ReactQueryDevtools } from 'react-query/devtools';
import ErrorBoundary from '@/components/error_boundary/ErrorBoundary';
import Root from '@/containers/Root';
import { LanguageProvider } from '@/containers/LanguageProvider';
import configureStore from './configureStore';
import { TRANSLATION_MESSAGES as translationMessages } from './translations';
import { initializeDayJS } from './utils/dayjs';
import { RecoilRoot } from 'recoil';
import { GlobalModal } from '@/containers/modal/Modal';
import { SERVICES } from '@/services/container';
import { ServicesContext } from '@/components/Services/Context';
import { AbilityProvider } from '@/containers/AuthenticatedApp/AbilityProvider';
import { AppQueryClient } from './containers/query_client/AppQueryClient';
import { AppAntConfigProvider } from './containers/ant-config-provider/AppAntConfigProvider';
import { AuthProvider } from '@/contexts/AuthContext';
import { createRoot } from 'react-dom/client';

initializeDayJS();

// Suppress benign ResizeObserver error across all browsers (Chrome, Firefox, Safari, Edge)
// This error doesn't affect functionality but can crash the page in Edge
window.addEventListener('error', e => {
  if (
    e.message === 'ResizeObserver loop limit exceeded' ||
    e.message === 'ResizeObserver loop completed with undelivered notifications.'
  ) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
});

// Additional handler for unhandled promise rejections
window.addEventListener('unhandledrejection', e => {
  if (e.reason?.message?.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
});

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://e65bcaccaba34e40b304ad68c9c476fb@o506301.ingest.us.sentry.io/5595837',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        blockAllMedia: false,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // fetch() rejected before any response (offline, sleep, navigation away) - Chrome / Firefox / Safari wording.
    // HTTP error responses throw our own error classes in utils/request.ts and are still reported.
    ignoreErrors: [
      /^TypeError: (Failed to fetch|NetworkError when attempting to fetch resource\.|Load failed)( \(.*\))?$/,
      // Chrome autofill / payment manager rejects internally with this string; not from application code.
      'Object Not Found Matching Id',
    ],
    // detect env by the hostname
    environment: window.location.hostname.includes('dev') ? 'development' : 'production',
    release: (import.meta.env.VITE_CIRCLE_BRANCH ?? '').replace(/\//g, '-').slice(0, 32), // '/' is not allowed in tag names in sentry, max 32 chars
  });
}

// Create redux store with history
const initialState = {};
export const store = configureStore(initialState, history, toast);

const container = document.getElementById('app');
const root = createRoot(container);

const MOUNT_NODE = document.getElementById('app');
const render = messages => {
  root.render(
    <ReduxProvider store={store}>
      <RecoilRoot>
        <ThemeProvider theme={theme}>
          <AppAntConfigProvider>
            <LanguageProvider messages={messages}>
              <ErrorBoundary>
                <AppQueryClient>
                  <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                  <ServicesContext.Provider value={SERVICES}>
                    <AbilityProvider>
                      <ConnectedRouter history={history}>
                        <AuthProvider>
                          <GlobalModal>
                            <Root />
                          </GlobalModal>
                        </AuthProvider>
                      </ConnectedRouter>
                    </AbilityProvider>
                  </ServicesContext.Provider>
                </AppQueryClient>
              </ErrorBoundary>
            </LanguageProvider>
          </AppAntConfigProvider>
        </ThemeProvider>
      </RecoilRoot>
    </ReduxProvider>,
    MOUNT_NODE,
  );
};

render(translationMessages);

// Install ServiceWorker and AppCache in the end since
if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'development') {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      // unregister all service workers
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    });
  }
}
